import { useState, useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Target,
  Briefcase,
  Lightbulb,
  MessageCircle,
  ChevronDown,
  ChevronRight,
  Check,
  Info,
} from 'lucide-react'
import type { LucideProps } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Facet, FacetCategory } from '@/types/interviewer'
import {
  groupFacetsByCategory,
  FACET_CATEGORY_CONFIG,
  FACET_TYPE_CONFIG,
} from '@/types/interviewer'
import { resolveEntityType } from '@/lib/interviewer/entityCreation'

// Icon mapping for categories
const CATEGORY_ICON_MAP: Record<FacetCategory, React.ComponentType<LucideProps>> = {
  market: Target,
  business: Briefcase,
  solution: Lightbulb,
  insights: MessageCircle,
}

interface FacetDetectionPanelProps {
  facets: Facet[]
  selectedFacetIds: Set<string>
  onToggleFacet: (facetId: string) => void
  onSelectAll: () => void
  onDeselectAll: () => void
}

export function FacetDetectionPanel({
  facets,
  selectedFacetIds,
  onToggleFacet,
  onSelectAll,
  onDeselectAll,
}: FacetDetectionPanelProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<FacetCategory>>(
    new Set(['market', 'business', 'solution', 'insights'])
  )

  // Group facets by category
  const groupedFacets = useMemo(() => {
    const activeFacets = facets.filter(f => !f.dismissed)
    return groupFacetsByCategory(activeFacets)
  }, [facets])

  // Count totals
  const totalFacets = Object.values(groupedFacets).flat().length
  const selectedCount = selectedFacetIds.size

  const toggleCategory = (category: FacetCategory) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  // Order categories as per plan
  const categoryOrder: FacetCategory[] = ['market', 'business', 'solution', 'insights']

  return (
    <div className="flex flex-col h-full">
      {/* Header with selection controls */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium">Detected Facets</h3>
          <p className="text-xs text-text-muted">
            {selectedCount} of {totalFacets} selected for mapping
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onSelectAll}
            className="text-xs"
          >
            Select All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDeselectAll}
            className="text-xs"
          >
            Deselect All
          </Button>
        </div>
      </div>

      {/* Category Accordions */}
      <div className="flex-1 overflow-auto space-y-2">
        {categoryOrder.map((category) => {
          const config = FACET_CATEGORY_CONFIG[category]
          const categoryFacets = groupedFacets[category]
          const CategoryIcon = CATEGORY_ICON_MAP[category]
          const isExpanded = expandedCategories.has(category)
          const selectedInCategory = categoryFacets.filter(f => selectedFacetIds.has(f.id)).length

          if (categoryFacets.length === 0) return null

          return (
            <div key={category} className="border border-warm-200 rounded-lg overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between px-3 py-2.5 bg-warm-50 hover:bg-warm-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-text-muted" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-text-muted" />
                  )}
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center"
                    style={{ backgroundColor: `${config.color}20` }}
                  >
                    <CategoryIcon className="w-3.5 h-3.5" style={{ color: config.color }} />
                  </div>
                  <span className="text-sm font-medium">{config.label}</span>
                  <Badge variant="outline" className="text-xs">
                    {categoryFacets.length}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  {selectedInCategory > 0 && (
                    <span className="text-xs text-solution">
                      {selectedInCategory} selected
                    </span>
                  )}
                </div>
              </button>

              {/* Category Content */}
              {isExpanded && (
                <div className="divide-y divide-warm-100">
                  {categoryFacets.map((facet) => (
                    <FacetItem
                      key={facet.id}
                      facet={facet}
                      isSelected={selectedFacetIds.has(facet.id)}
                      onToggle={() => onToggleFacet(facet.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Info footer */}
      <div className="mt-4 pt-3 border-t border-warm-200">
        <div className="flex items-start gap-2 text-xs text-text-muted">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <p>
            Select the facets you want to map to your ontology. You can review and decide
            how each facet maps in the next step.
          </p>
        </div>
      </div>
    </div>
  )
}

// Individual facet item
function FacetItem({
  facet,
  isSelected,
  onToggle,
}: {
  facet: Facet
  isSelected: boolean
  onToggle: () => void
}) {
  const typeConfig = FACET_TYPE_CONFIG[facet.type]

  return (
    <div
      className={cn(
        'flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors',
        isSelected ? 'bg-solution/5' : 'hover:bg-warm-50'
      )}
      onClick={onToggle}
    >
      {/* Checkbox */}
      <div
        className={cn(
          'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
          isSelected
            ? 'bg-solution border-solution'
            : 'border-warm-300'
        )}
      >
        {isSelected && <Check className="w-3 h-3 text-white" />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Badge
            variant="outline"
            className="text-[10px] px-1.5"
            style={{ borderColor: typeConfig.color, color: typeConfig.color }}
          >
            {typeConfig.label}
          </Badge>
          {facet.confidence && (
            <span className="text-[10px] text-text-muted">
              {Math.round(facet.confidence * 100)}% confidence
            </span>
          )}
        </div>
        <p className="text-sm line-clamp-2">{facet.content}</p>
        {facet.evidence && (
          <p className="text-xs text-text-muted mt-1 line-clamp-1 italic">
            "{facet.evidence}"
          </p>
        )}
      </div>

      {/* AI suggestion indicator - show resolved entity type */}
      {facet.suggestedOntologyType && (() => {
        const resolved = resolveEntityType(facet.suggestedOntologyType)
        if (resolved.isInsightOnly) {
          return (
            <div className="flex-shrink-0">
              <Badge variant="outline" className="text-[10px] bg-warm-100 border-warm-300 text-text-muted">
                insight only
              </Badge>
            </div>
          )
        }
        return (
          <div className="flex-shrink-0">
            <Badge variant="outline" className="text-[10px] bg-solution/10 border-solution text-solution">
              → {resolved.entityType}
            </Badge>
          </div>
        )
      })()}
    </div>
  )
}
